import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Scissors,
  XCircle,
  Loader2,
  Clock,
  DollarSign,
  Sparkles,
} from "lucide-react";
import { timeEntriesApi, mattersApi } from "../../services/api";
import SearchableSelect, {
  SelectOption,
} from "../ui/SearchableSelect";
import SplitEntryModal from "./SplitEntryModal";

interface ReviewEntry {
  _id: string;
  narrative: string;
  date: string;
  minutes: number;
  rateCents: number;
  amountCents: number;
  confidence?: number;
  suggestedMatters?: Array<{
    matterId: string;
    matterTitle: string;
    confidence: number;
  }>;
  matterId?: any;
}

interface Props {
  onDataChange?: () => void;
}

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const confidenceBadge = (confidence: number) => {
  if (confidence >= 80) {
    return "bg-emerald-100 text-emerald-700";
  }
  if (confidence >= 50) {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-red-100 text-red-700";
};

const NeedsReviewTab: React.FC<Props> = ({ onDataChange }) => {
  const [entries, setEntries] = useState<ReviewEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [matterOptions, setMatterOptions] = useState<SelectOption[]>([]);
  const [manualSelections, setManualSelections] = useState<
    Record<string, string | null>
  >({});
  const [assigningIds, setAssigningIds] = useState<Set<string>>(new Set());
  const [splitEntry, setSplitEntry] = useState<ReviewEntry | null>(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await timeEntriesApi.getReviewEntries();
      setEntries(res.data?.data ?? res.data ?? []);
    } catch (err) {
      console.error("Failed to load review entries", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMatters = useCallback(async () => {
    try {
      const res = await mattersApi.list({ limit: 200, status: "open" });
      const matters = res.data?.data ?? res.data ?? [];
      setMatterOptions(
        matters.map((m: any) => ({
          value: m._id,
          label: m.title || m.name || m._id,
        }))
      );
    } catch (err) {
      console.error("Failed to load matters", err);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchMatters();
  }, [fetchEntries, fetchMatters]);

  const handleRefresh = useCallback(() => {
    fetchEntries();
    onDataChange?.();
  }, [fetchEntries, onDataChange]);

  const handleAssignMatter = useCallback(
    async (entryId: string, matterId: string) => {
      setAssigningIds((prev) => new Set(prev).add(entryId));
      try {
        await timeEntriesApi.assignMatter(entryId, { matterId });
        handleRefresh();
      } catch (err) {
        console.error("Failed to assign matter", err);
      } finally {
        setAssigningIds((prev) => {
          const next = new Set(prev);
          next.delete(entryId);
          return next;
        });
      }
    },
    [handleRefresh]
  );

  const handleDismiss = useCallback(
    async (entryId: string) => {
      try {
        await timeEntriesApi.dismissSuggestion(entryId);
        handleRefresh();
      } catch (err) {
        console.error("Failed to dismiss entry", err);
      }
    },
    [handleRefresh]
  );

  /* ── Loading state ─────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  /* ── Empty state ───────────────────────────────────────────────── */
  if (entries.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="flex flex-col items-center justify-center py-20"
      >
        <div className="relative mb-6">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
          <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
            <CheckCircle2 className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
          </div>
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">All caught up!</h3>
        <p className="text-sm text-slate-500 text-center max-w-md leading-relaxed">
          No time entries need review right now. New AI-captured entries will appear here for your approval.
        </p>
      </motion.div>
    );
  }

  /* ── Entry list ────────────────────────────────────────────────── */
  return (
    <>
      <motion.div
        className="space-y-4"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.06 } },
        }}
      >
        <AnimatePresence mode="popLayout">
          {entries.map((entry) => {
            const manualMatter = manualSelections[entry._id] ?? null;
            const isAssigning = assigningIds.has(entry._id);

            return (
              <motion.div
                key={entry._id}
                layout
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0 },
                }}
                exit={{ opacity: 0, x: -40, transition: { duration: 0.2 } }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-4 mb-3">
                  <p className="text-sm text-slate-800 leading-relaxed flex-1">
                    {entry.narrative}
                  </p>
                  <span className="inline-flex items-center rounded-full bg-purple-100 text-purple-700 px-2.5 py-0.5 text-xs font-medium whitespace-nowrap">
                    Review
                  </span>
                </div>

                {/* Meta row */}
                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatDate(entry.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatDuration(entry.minutes)}
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign className="h-3.5 w-3.5" />
                    {formatCurrency(entry.amountCents)}
                  </span>
                  {entry.confidence != null && (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${confidenceBadge(
                        entry.confidence
                      )}`}
                    >
                      {entry.confidence}% confidence
                    </span>
                  )}
                </div>

                {/* AI Suggested Matters */}
                {entry.suggestedMatters && entry.suggestedMatters.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                      AI Suggested Matters
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {entry.suggestedMatters.map((sm) => (
                        <button
                          key={sm.matterId}
                          disabled={isAssigning}
                          onClick={() =>
                            handleAssignMatter(entry._id, sm.matterId)
                          }
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                        >
                          <span className="font-medium">{sm.matterTitle}</span>
                          <span
                            className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${confidenceBadge(
                              sm.confidence
                            )}`}
                          >
                            {sm.confidence}%
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual Assignment */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      options={matterOptions}
                      value={manualMatter}
                      onChange={(val) =>
                        setManualSelections((prev) => ({
                          ...prev,
                          [entry._id]: val,
                        }))
                      }
                      placeholder="Select a matter..."
                      searchPlaceholder="Search matters..."
                    />
                  </div>
                  <button
                    disabled={!manualMatter || isAssigning}
                    onClick={() =>
                      manualMatter &&
                      handleAssignMatter(entry._id, manualMatter)
                    }
                    className="inline-flex items-center justify-center rounded-xl bg-[#0F5132] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#146C43] transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isAssigning ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Assign"
                    )}
                  </button>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <button
                    onClick={() => setSplitEntry(entry)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <Scissors className="h-3.5 w-3.5" />
                    Split
                  </button>
                  <button
                    onClick={() => handleDismiss(entry._id)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Dismiss
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {/* Split modal */}
      {splitEntry && (
        <SplitEntryModal
          entry={splitEntry}
          onClose={() => setSplitEntry(null)}
          onSplit={() => {
            setSplitEntry(null);
            handleRefresh();
          }}
        />
      )}
    </>
  );
};

export default NeedsReviewTab;
